import { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../App';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to detect subdomain
const getSubdomain = () => {
  const host = window.location.hostname;
  const parts = host.split('.');
  
  // Skip if localhost or IP
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return null;
  }
  
  // If we have 3+ parts (subdomain.domain.tld)
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip common subdomains
    if (!['www', 'api', 'admin', 'app'].includes(subdomain)) {
      return subdomain;
    }
  }
  
  // For development: company.localhost format
  if (parts.length === 2 && parts[1] === 'localhost') {
    return parts[0];
  }
  
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [subdomain, setSubdomain] = useState(getSubdomain());
  const [companyInfo, setCompanyInfo] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${savedToken}`
            }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(savedToken);
            
            // Set company info from user
            if (userData.company_name) {
              setCompanyInfo({
                name: userData.company_name,
                id: userData.company_id
              });
            }
          } else {
            // Invalid token
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Fetch company info if subdomain exists
  useEffect(() => {
    const fetchCompanyBySubdomain = async () => {
      if (subdomain && !companyInfo) {
        try {
          const response = await fetch(`${API}/company/by-subdomain/${subdomain}`);
          if (response.ok) {
            const data = await response.json();
            setCompanyInfo(data);
          }
          // Silently ignore 404 - subdomain might not be a company
        } catch (error) {
          // Silently ignore network errors for subdomain lookup
        }
      }
    };
    fetchCompanyBySubdomain();
  }, [subdomain, companyInfo]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'فشل تسجيل الدخول');
      }
      
      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      
      // Update company info
      if (data.user.company_name) {
        setCompanyInfo({
          name: data.user.company_name,
          id: data.user.company_id
        });
      }
      
      return data.user;
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        throw new Error('لا يمكن الاتصال بالخادم. تأكد من اتصالك بالإنترنت.');
      }
      throw error;
    }
  };

  const register = async (name, email, password, companyId = null, isInitialSetup = false) => {
    const body = { name, email, password };
    if (companyId) {
      body.company_id = companyId;
    }
    
    // Use initial-setup endpoint if this is first user
    const endpoint = isInitialSetup ? `${API}/auth/initial-setup` : `${API}/auth/register`;
    
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'فشل التسجيل');
      }
      
      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        throw new Error('لا يمكن الاتصال بالخادم. تأكد من اتصالك بالإنترنت.');
      }
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Refresh user data from server (used after profile updates)
  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isCompanyAdmin: user?.role === 'company_admin',
    isEmployee: user?.role === 'employee',
    subdomain,
    companyInfo,
    // Helper to make authenticated API calls
    authFetch: async (url, options = {}) => {
      const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };
      return fetch(url, { ...options, headers });
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
